import { CalendarCheck, Contact, IndianRupee, MessagesSquare, Milestone, Phone, Trophy, UserPlus } from 'lucide-react';

// AUTO-CRM-32: words, icons and colours for targets, shared by the Targets page,
// Home and Team.
export const METRICS = {
  new_leads: { label: 'New leads', unit: 'leads', icon: UserPlus, issue: 'Lead flow', verb: 'bring in', order: 1 },
  leads_contacted: { label: 'Leads contacted', unit: 'leads', icon: Contact, issue: 'Reaching leads', verb: 'contact', order: 2 },
  calls: { label: 'Calls made', unit: 'calls', icon: Phone, issue: 'Calling', verb: 'make', order: 3 },
  conversations: { label: 'Conversations', unit: 'conversations', icon: MessagesSquare, issue: 'Follow-through', verb: 'have', order: 4 },
  meetings: { label: 'Meetings held', unit: 'meetings', icon: CalendarCheck, issue: 'Meetings', verb: 'hold', order: 5 },
  stage_reached: { label: 'Leads reaching a stage', unit: 'leads', icon: Milestone, issue: 'Moving leads forward', verb: 'move', order: 6 },
  won: { label: 'Deals won', unit: 'deals', icon: Trophy, issue: 'Closing', verb: 'win', order: 7 },
  revenue: { label: 'Revenue won', unit: 'currency', icon: IndianRupee, issue: 'Revenue', verb: 'win', order: 8 },
};
export const PERIODS = {
  daily: { label: 'Daily', this: 'today', noun: 'day', section: 'Today' },
  weekly: { label: 'Weekly', this: 'this week', noun: 'week', section: 'This week' },
  monthly: { label: 'Monthly', this: 'this month', noun: 'month', section: 'This month' },
  quarterly: { label: 'Quarterly', this: 'this quarter', noun: 'quarter', section: 'This quarter' },
};
export const STATUS = {
  done: { label: 'Hit', tone: 'done' },
  ahead: { label: 'Ahead', tone: 'ahead' },
  on_track: { label: 'On track', tone: 'ok' },
  behind: { label: 'Behind', tone: 'behind' },
  at_risk: { label: 'At risk', tone: 'risk' },
  missed: { label: 'Missed', tone: 'risk' },
};
// Worst first: what needs attention.
export const STATUS_RANK = { missed: 0, at_risk: 0, behind: 1, on_track: 2, ahead: 3, done: 4 };
export const NEEDS_WORK = ['missed', 'at_risk', 'behind'];

const inr = (value) => {
  const number = Number(value) || 0;
  if (number >= 10000000) return `₹${(number / 10000000).toFixed(number >= 100000000 ? 0 : 1).replace(/\.0$/, '')} Cr`;
  if (number >= 100000) return `₹${(number / 100000).toFixed(number >= 1000000 ? 0 : 1).replace(/\.0$/, '')} L`;
  return `₹${Math.round(number).toLocaleString('en-IN')}`;
};

export function formatValue(target, value) {
  if (target?.unit === 'currency' || target?.metric === 'revenue') return inr(value);
  const number = Number(value) || 0;
  return (Number.isInteger(number) ? number : Math.round(number * 10) / 10).toLocaleString('en-IN');
}

export const unitWord = (target, count = 2) => {
  if (target?.metric === 'stage_reached') return count === 1 ? 'lead' : 'leads';
  const unit = METRICS[target?.metric]?.unit || target?.unit || 'items';
  if (unit === 'currency') return '';
  return count === 1 ? unit.replace(/s$/, '') : unit;
};

export function targetTitle(target) {
  if (target.name) return target.name;
  if (target.metric === 'stage_reached' && target.stage?.name) return `Leads reaching ${target.stage.name}`;
  return METRICS[target.metric]?.label || target.metric_label || 'Target';
}

// "Calling", "Lead flow", "Getting to Site visit"…
export function issueName(target) {
  if (target.metric === 'stage_reached' && target.stage?.name) return `Getting leads to ${target.stage.name}`;
  return METRICS[target.metric]?.issue || targetTitle(target);
}

// "this month", or "that month" when looking back at a finished period.
export const periodPhrase = (period, finished = false) => (finished ? `that ${PERIODS[period]?.noun || 'period'}` : PERIODS[period]?.this || '');

export function paceLine(target, finished = false) {
  const progress = target.progress || {};
  if (finished && progress.status !== 'done') return `Ended ${formatValue(target, progress.remaining)} ${unitWord(target, progress.remaining)} short.`.replace(/\s+short/, ' short');
  if (progress.status === 'done') {
    const over = progress.actual - target.target_value;
    return over > 0 ? `Hit, with ${formatValue(target, over)} extra.` : 'Hit.';
  }
  if (!progress.remaining) return '';
  const money = target.metric === 'revenue';
  if (target.period === 'daily') return `${formatValue(target, money ? progress.remaining : Math.ceil(progress.remaining))} more ${unitWord(target, progress.remaining)} to go today.`;
  const days = progress.days_left;
  const left = `${days} ${days === 1 ? 'day' : 'days'} left`;
  // Counts are whole; below one a day, a weekly number reads better.
  if (!money && progress.per_day_needed < 1 && days >= 7) {
    const perWeek = Math.ceil(progress.per_day_needed * 7);
    return `${perWeek} ${unitWord(target, perWeek)} a week hits it (${left}).`;
  }
  const perDay = money ? progress.per_day_needed : Math.ceil(progress.per_day_needed);
  return `${formatValue(target, perDay)} ${unitWord(target, perDay)} a day hits it (${left}).`;
}
